import { LegacyRef, useState, ReactNode } from "react";

import { type FormInstance, Input } from "antd";
import { clsx } from "clsx";

import { RVIcon, RVIconSizeOptions, RVRow, RVFormItem } from "@regal-voice/ui";

import { DrawerPanelIcon } from "../DrawerPanelIcon";

import styles from "./DrawerPanel.module.scss";

export function DrawerTitle({
    title,
    editable,
    name,
    leftSideIcon,
    rightSideIcon,
    dragHandleRef,
}: {
    title: string;
    name?: string | number | Array<string | number>;
    editable?: boolean;
    form?: FormInstance;
    leftSideIcon?: ReactNode;
    rightSideIcon?: ReactNode;
    dragHandleRef?: LegacyRef<HTMLDivElement>;
}): JSX.Element {
    const draggable = !!dragHandleRef;

    const [editing, setEditing] = useState(false);
    const [displayTitle, setDisplayTitle] = useState(title);

    function toggleIsEditing() {
        setEditing(!editing);
    }

    function onBlur(event: React.FocusEvent) {
        event.preventDefault();
        if (editing) {
            setEditing(false);
            setDisplayTitle((event.currentTarget as HTMLInputElement).value);
        }
    }

    function handleKeyDown(event: React.KeyboardEvent) {
        if (["Enter", "Escape"].includes(event.key)) {
            if (editing) {
                setEditing(false);
            }
            event.stopPropagation();
            event.preventDefault();
        }

        if (event.key === "Enter") {
            setDisplayTitle((event.currentTarget as HTMLInputElement).value);
        }
    }

    // This prevents the panel from expanding/collapsing when clicked
    function handleOnClick(event: React.MouseEvent) {
        event.stopPropagation();
        event.preventDefault();
    }

    return (
        <RVRow alignItems="center" className={styles.title} data-testid={`drawer-panel-title-${title}`}>
            <RVRow alignItems="center" gap={1} flex="1">
                {draggable && (
                    <div
                        className={clsx("drawer-panel-drag-handle", styles.dragArea)}
                        {...(draggable ? { ref: dragHandleRef } : {})}
                    >
                        <RVIcon
                            className={clsx(styles.icon, styles.dragHandle)}
                            icon="drag-handle"
                            size={RVIconSizeOptions.XSMALL}
                        />
                    </div>
                )}
                {leftSideIcon}
                <RVRow alignItems="center" gap={2} flex="1">
                    {editable && editing ? (
                        <RVFormItem name={name} noStyle>
                            <Input
                                autoFocus
                                className={styles.drawerTitleInput}
                                data-testid={`drawer-title-${title}-input`}
                                placeholder={displayTitle}
                                onBlur={onBlur}
                                onKeyDown={handleKeyDown}
                                onClick={handleOnClick}
                            />
                        </RVFormItem>
                    ) : (
                        <div data-testid={`drawer-panel-title-${title}-display-title`}>{displayTitle}</div>
                    )}
                    {editable && !editing && <DrawerPanelIcon icon="edit-form" edit onClick={toggleIsEditing} />}
                </RVRow>
            </RVRow>

            <RVRow alignItems="center" gap={2}>
                {rightSideIcon}
            </RVRow>
        </RVRow>
    );
}
